import React, { useEffect, useState } from 'react';
import { message } from 'antd';
import {
  TWInput,
  TWButton,
  ToggleSwitch,
  FieldLabel,
} from '../../../components/ui';
import referralAdminApi from '../../../services/referralAdminApi';

const EMPTY = {
  enabled: true,
  rewardamount: 0,
  expirationdays: 0,
  minimumorders: 0,
  registerurltemplate: '',
  sharesmstemplate: '',
  shareemailsubject: '',
  shareemailbody: '',
  fraudiplookbackdays: 0,
};

const textareaCls =
  'block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 transition focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder-slate-500';

const ReferralSettingsForm = ({ settings, loading, onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings) {
      setForm({ ...EMPTY, ...settings });
    }
  }, [settings]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        rewardamount: Number(form.rewardamount) || 0,
        expirationdays: Number(form.expirationdays) || 0,
        minimumorders: Number(form.minimumorders) || 0,
        fraudiplookbackdays: Number(form.fraudiplookbackdays) || 0,
      };
      await referralAdminApi.updateSettings(body);
      message.success('Settings saved');
      onSaved();
    } catch (err) {
      const apiMessage =
        typeof err?.response?.data === 'string'
          ? err.response.data
          : err?.response?.data?.message || 'Failed to save settings';
      message.error(apiMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Loading settings…
      </p>
    );
  }

  return (
    <div className="reveal max-w-2xl space-y-5 rounded-md border border-slate-200 bg-white p-5 dark:border-slate-700 dark:bg-slate-800/70 sm:p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Program enabled
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Turn the referral program on or off globally.
          </p>
        </div>
        <ToggleSwitch
          checked={form.enabled}
          onChange={(v) => setField('enabled', v)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div>
          <FieldLabel>Reward amount ($)</FieldLabel>
          <TWInput
            type="number"
            value={form.rewardamount}
            onChange={(e) => setField('rewardamount', e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Expiration (days)</FieldLabel>
          <TWInput
            type="number"
            value={form.expirationdays}
            onChange={(e) => setField('expirationdays', e.target.value)}
          />
        </div>
        <div>
          <FieldLabel>Minimum orders</FieldLabel>
          <TWInput
            type="number"
            value={form.minimumorders}
            onChange={(e) => setField('minimumorders', e.target.value)}
          />
        </div>
      </div>

      <div className="sm:max-w-[12rem]">
        <FieldLabel>Fraud IP lookback (days)</FieldLabel>
        <TWInput
          type="number"
          value={form.fraudiplookbackdays}
          onChange={(e) => setField('fraudiplookbackdays', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel>Register URL template</FieldLabel>
        <TWInput
          value={form.registerurltemplate}
          onChange={(e) => setField('registerurltemplate', e.target.value)}
          placeholder="https://thisway.co/register?ref={code}"
        />
      </div>

      <div>
        <FieldLabel>Share SMS template</FieldLabel>
        <textarea
          rows={3}
          className={textareaCls}
          value={form.sharesmstemplate}
          onChange={(e) => setField('sharesmstemplate', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel>Share email subject</FieldLabel>
        <TWInput
          value={form.shareemailsubject}
          onChange={(e) => setField('shareemailsubject', e.target.value)}
        />
      </div>

      <div>
        <FieldLabel>Share email body</FieldLabel>
        <textarea
          rows={4}
          className={textareaCls}
          value={form.shareemailbody}
          onChange={(e) => setField('shareemailbody', e.target.value)}
        />
      </div>

      <div className="flex justify-end border-t border-slate-100 pt-4 dark:border-slate-700/60">
        <TWButton
          variant="primary"
          label={saving ? 'Saving…' : 'Save changes'}
          loading={saving}
          onClick={handleSave}
        />
      </div>
    </div>
  );
};

export default ReferralSettingsForm;
